import moment from "moment-timezone";
import { ALL_EQUIPMENT, MY_LAB } from "../../constants";

export const buildSavedFilterOptions = ({ myLabFilter, userFilters = [], signeeFilter }) => {
  const myLabOptions = myLabFilter ? [{ name: MY_LAB, label: "My Lab" }] : [];
  const allEquipmentOptions =
    signeeFilter && signeeFilter !== "All users"
      ? [{ name: ALL_EQUIPMENT, label: "All Equipment" }]
      : [];

  const filterOptions = userFilters
    .filter((filter) => filter.filterName && filter.filterName !== MY_LAB)
    .map((filter) => ({ name: filter.filterName, label: filter.filterName }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  return [...myLabOptions, ...allEquipmentOptions, ...filterOptions];
};

export const getResetSavedFilterValue = ({ isGroupView, savedFilterOptions }) => {
  if (isGroupView) {
    const pathSegments = window.location.pathname.split("/");
    const groupFilterName = pathSegments[pathSegments.length - 1];
    const groupFilterOption = savedFilterOptions.find((option) => option.name === groupFilterName);
    return groupFilterOption?.name || savedFilterOptions[0].name;
  }

  const myLabOption = savedFilterOptions.find((option) => option.name === MY_LAB);
  return myLabOption ? MY_LAB : savedFilterOptions[0].name;
};

export const getInitialSavedFilterSelection = ({
  savedFilter,
  savedFilterOptions,
  isGroupView,
  savedFilterStorageKey,
  storageOptions,
  getPersistedFilter,
}) => {
  const hasOptions = savedFilterOptions.length > 0;
  if (!hasOptions) return null;

  const missingAllEquipmentOption =
    savedFilter === ALL_EQUIPMENT && !savedFilterOptions.some((item) => item.name === ALL_EQUIPMENT);
  const missingMyLabOption =
    savedFilter === MY_LAB && !savedFilterOptions.some((item) => item.name === MY_LAB);

  if (savedFilter && !missingAllEquipmentOption && !missingMyLabOption) {
    return null;
  }

  const persistedFilter = getPersistedFilter(savedFilterStorageKey, null, storageOptions);
  return isGroupView ? persistedFilter || savedFilterOptions[0].name : savedFilterOptions[0].name;
};

export const getSavedFilterChangeResult = ({
  selectedFilterName,
  signeeFilter,
  isGroupView,
  allUsers,
  userId,
}) => {
  if (isGroupView) {
    return {
      shouldIgnoreSelection: true,
      shouldUpdateSavedFilter: false,
      nextSigneeFilter: null,
    };
  }

  const shouldIgnoreSelection =
    selectedFilterName === "" && (!signeeFilter || signeeFilter === "" || signeeFilter === "All users");

  const shouldUpdateSavedFilter = !isGroupView;

  let nextSigneeFilter = null;
  if (selectedFilterName === "" && signeeFilter === "") {
    nextSigneeFilter = allUsers.find((userData) => userData.userId === userId) || null;
  }

  return {
    shouldIgnoreSelection,
    shouldUpdateSavedFilter,
    nextSigneeFilter,
  };
};

export const getSigneeFilterChangeResult = ({ selectedSignee, allUsers, savedFilter }) => {
  const nextSigneeFilter =
    selectedSignee.name === ""
      ? ""
      : selectedSignee.data || allUsers.find((userData) => userData.userId === selectedSignee.name) || selectedSignee;

  const shouldSwitchToMyLab = selectedSignee.name === "" && savedFilter === "" && savedFilter !== ALL_EQUIPMENT;
  return { nextSigneeFilter, shouldSwitchToMyLab };
};

export const getTvDateLabel = (timezone) => {
  try {
    const tz = timezone || "UTC";
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date());
  } catch {
    return new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
};

const getDefaultSavedFilter = (savedFilterOptions) => {
  const myLabOption = savedFilterOptions.find((option) => option.name === MY_LAB);
  if (myLabOption) return MY_LAB;
  return savedFilterOptions[0].name;
};

const isEmptySigneeFilter = (signeeFilter) => {
  if (signeeFilter === "") return true;
  if (typeof signeeFilter !== "object") return false;
  return !signeeFilter.userId || signeeFilter.userId === "";
};

const hasDateRangeChanged = (selectedDateRange, getDefaultDateRange) => {
  const [defaultRangeStart, defaultRangeEnd] = getDefaultDateRange();
  if (!selectedDateRange) return true;
  if (selectedDateRange.length !== 2) return true;
  if (!moment(selectedDateRange[0]).isSame(defaultRangeStart, "day")) return true;
  if (!moment(selectedDateRange[1]).isSame(defaultRangeEnd, "day")) return true;
  return false;
};

export const computeIsResetDisabled = ({
  savedFilterOptions,
  enableResetAll,
  signeeFilter,
  savedFilter,
  searchQuery,
  displayPreset,
  isGroupView,
  selectedDateRange,
  getDefaultDateRange,
}) => {
  if (savedFilterOptions.length === 0) return true;
  if (enableResetAll) return false;

  const defaultSavedFilter = getDefaultSavedFilter(savedFilterOptions);
  const filterIsAtDefault = savedFilter === defaultSavedFilter || isGroupView;
  if (!filterIsAtDefault) return false;

  if (!isEmptySigneeFilter(signeeFilter)) return false;
  if (searchQuery !== "") return false;
  if (displayPreset !== null) return false;
  if (hasDateRangeChanged(selectedDateRange, getDefaultDateRange)) return false;

  return true;
};
